import React, { useState } from 'react';
import { getFeedback } from '../services/feedback';
import FeedbackDisplay from './FeedbackDisplay';
import { isTrial } from '../data/storage';
import { canWriteEssay, getSubscriptionStatus, getDaysRemaining } from '../utils/subscription';

const TRIAL_PROMPT = {
  id: 'trial-fixed',
  title: 'Rewarding Students',
  text: 'Some people say that students who achieve the highest scores in their exams should be rewarded. Others say that those who show progress should be rewarded instead. Discuss both views and give your own opinion.',
  type: 'discuss',
  year: 'Trial Question',
};

function getParaAccent(paraId) {
  const m = { intro:'#2a6099', body1:'#2a6099', body2:'#2a6099', conclusion:'#2a6099' };
  return m[paraId] || '#2a6099';
}

// ── EXAM COLOURS ─────────────────────────────────────────────────
const E = {
  bg:        '#ffffff',
  pageBg:    '#f5f5f5',
  border:    '#c0c0c0',
  borderFocus:'#1a5fa8',
  text:      '#1a1a1a',
  textLight: '#444444',
  muted:     '#666666',
  label:     '#333333',
  accent:    '#1a5fa8',
  accentBg:  '#e8f0fb',
  success:   '#1a6b3a',
  successBg: '#e8f5ee',
  warning:   '#7a4a00',
  warningBg: '#fff8e8',
  error:     '#8b1a1a',
  errorBg:   '#fff0f0',
  btnPrimary:'#1a5fa8',
  btnText:   '#ffffff',
  navBg:     '#1a2a4a',
  navText:   '#ffffff',
  gold:      '#b8860b',
};

function SubscriptionBlock({ status, onBack }) {
  return (
    <div style={{ minHeight:'100vh', background:'#f8f9fb', display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ maxWidth:480, width:'100%', background:'#fff', border:'1px solid #e8edf5', borderRadius:16, padding:32, boxShadow:'0 4px 24px rgba(26,42,74,0.09)', textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:14 }}>🔒</div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>
          {status === 'expired' ? 'Subscription Expired' : 'Access Restricted'}
        </div>
        <div style={{ fontSize:13.5, color:'#5a6a88', lineHeight:1.7, marginBottom:22 }}>
          {status === 'expired'
            ? 'Your subscription has expired. Contact Mr. Ahmed Salem to renew and regain access.'
            : 'Your subscription is not active. Contact Mr. Ahmed Salem to activate your access.'}
        </div>
        <a href="https://wa.me/201008182425" target="_blank" rel="noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#25d366', color:'#fff', borderRadius:10, padding:'12px 22px', fontSize:14, fontWeight:700, textDecoration:'none', marginBottom:16 }}>
          Contact Mr. Ahmed Salem
        </a>
        <div><button onClick={onBack} style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace" }}>← Back</button></div>
      </div>
    </div>
  );
}

export default function EssayScreen({ branding, candidate, prompt: promptProp, essayType, formula, onBack, onUsed }) {
  // Trial users always get the fixed prompt
  const isTrialUser = isTrial(candidate);
  const prompt = isTrialUser ? TRIAL_PROMPT : promptProp;

  // Subscription check (computed before hooks, applied after)
  const canWrite  = canWriteEssay(candidate);
  const subStatus = canWrite ? null : getSubscriptionStatus(candidate);

  const [activePara, setActivePara] = useState(0);
  const [writings,   setWritings]   = useState({});
  const [feedback,   setFeedback]   = useState({});
  const [loadingFb,  setLoadingFb]  = useState({});
  const [fbError,    setFbError]    = useState({});
  const [showF,      setShowF]      = useState({});
  const [showIdeas,  setShowIdeas]  = useState({});
  const [selOpt,     setSelOpt]     = useState({});
  const [submitted,  setSubmitted]  = useState(false);

  const paras   = formula || [];
  const para    = paras[activePara];
  const totalWC = Object.values(writings).join(' ').split(/\s+/).filter(Boolean).length;
  const allDone = paras.every(p => p.sentences.every(s => (writings[p.id+'_'+s.num]||'').trim().length > 5));
  const paraDone= para?.sentences.every(s => (writings[para.id+'_'+s.num]||'').trim().length > 8);
  const fb      = feedback[para?.id];

  function setW(key, val) { setWritings(w => ({...w, [key]:val})); }

  async function handleFeedback() {
    setFbError(e => ({...e, [para.id]:''}));
    setLoadingFb(l => ({...l, [para.id]:true}));
    try {
      const ws  = para.sentences.map(s => writings[para.id+'_'+s.num]||'');
      const res = await getFeedback({ promptText:prompt.text, essayTypeLabel:essayType.label, paraLabel:para.label, sentences:para.sentences, writings:ws, isTrialUser });
      setFeedback(f => ({...f, [para.id]:res}));
      // essay_scores insert handled centrally by getFeedback in feedback.js
    } catch(e) {
      setFbError(err => ({...err, [para.id]:e.message}));
    }
    setLoadingFb(l => ({...l, [para.id]:false}));
  }

  function handleSubmit() {
    onUsed && onUsed('smart');
    setSubmitted(true);
  }

  // Subscription gate (rendered after all hooks)
  if (!canWrite) return <SubscriptionBlock status={subStatus} onBack={onBack}/>;

  if (!para) return null;

  const paraLabels = { intro:'Introduction', body1:'Body Paragraph 1', body2:'Body Paragraph 2', conclusion:'Conclusion' };

  return (
    <div style={{ minHeight:'100vh', background:E.pageBg, display:'flex', flexDirection:'column', fontFamily:"'Times New Roman', Times, serif" }}>

      {/* ── TOP NAV — exam style ── */}
      <div style={{ background:E.navBg, padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:200, boxShadow:'0 2px 4px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={onBack} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)', color:'#ffffff', padding:'5px 13px', borderRadius:5, fontSize:12, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            ← Back
          </button>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:600, color:'#ffffff' }}>
            IELTS<span style={{color:E.gold}}>COMPASS</span>
            <span style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginLeft:8, fontWeight:400 }}>Smart Writing Mode</span>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ fontFamily:"'Courier New', monospace", fontSize:12, color:totalWC>=250?'#7adb98':'rgba(255,255,255,0.65)', background:'rgba(0,0,0,0.2)', padding:'4px 12px', borderRadius:4 }}>
            {totalWC} / 250 words minimum {totalWC>=250?'✓':''}
          </div>
        </div>
      </div>

      {/* ── QUESTION PANEL — exam style ── */}
      <div style={{ background:'#ffffff', borderBottom:'2px solid #c0c0c0', padding:'14px 24px' }}>
        <div style={{ maxWidth:860, margin:'0 auto' }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, fontWeight:600, letterSpacing:'.15em', textTransform:'uppercase', color:E.accent, marginBottom:6 }}>
            {essayType.label}
          </div>
          <div style={{ fontSize:14.5, color:E.text, lineHeight:1.75, fontWeight:400 }}>
            {prompt.text}
          </div>
          <div style={{ marginTop:8, fontSize:12, color:E.muted, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Write at least 250 words. You should spend about 40 minutes on this task.
          </div>
          {isTrialUser && (
            <div style={{ marginTop:10, background:'#fff8f0', border:'1px solid #f0d890', borderRadius:6, padding:'8px 12px', fontSize:12, color:'#7a4a00', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              📝 This is your free trial question. <a href="https://wa.me/201008182425" target="_blank" rel="noreferrer" style={{ color:'#b8860b', fontWeight:600 }}>Upgrade to access our full bank of 50+ real IELTS exam questions →</a>
            </div>
          )}
        </div>
      </div>

      {/* ── PARAGRAPH TABS — exam style ── */}
      <div style={{ background:'#ffffff', borderBottom:'1px solid #c0c0c0', display:'flex', overflowX:'auto' }}>
        <div style={{ maxWidth:860, margin:'0 auto', display:'flex', width:'100%' }}>
          {paras.map((p, i) => {
            const done  = p.sentences.every(s => (writings[p.id+'_'+s.num]||'').trim().length > 8);
            const hasFb = !!feedback[p.id];
            const isOn  = activePara === i;
            return (
              <button key={p.id} onClick={() => setActivePara(i)}
                style={{ flex:1, minWidth:80, padding:'10px 6px', border:'none', borderBottom:`3px solid ${isOn?E.btnPrimary:'transparent'}`, background:isOn?E.accentBg:'transparent', color:isOn?E.btnPrimary:E.muted, fontSize:12.5, fontWeight:isOn?600:400, cursor:'pointer', transition:'all .15s', whiteSpace:'nowrap', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                <div style={{ fontSize:9, fontFamily:"'Courier New',monospace", marginBottom:1, opacity:.7 }}>{p.para}</div>
                {paraLabels[p.id]||p.id}
                {done && <span style={{ marginLeft:5, color:hasFb?E.success:E.gold, fontSize:10 }}>{hasFb?'✓':'○'}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── MAIN WRITING AREA ── */}
      <div style={{ maxWidth:860, margin:'0 auto', width:'100%', padding:'20px 20px', flex:1 }}>

        {/* Para header */}
        <div style={{ background:'#ffffff', border:'1px solid #c0c0c0', borderLeft:`4px solid ${E.btnPrimary}`, borderRadius:'0 6px 6px 0', padding:'10px 16px', marginBottom:18 }}>
          <div style={{ fontFamily:"'Courier New',monospace", fontSize:10, color:E.accent, letterSpacing:'.1em', textTransform:'uppercase', marginBottom:2 }}>{para.para}</div>
          <div style={{ fontSize:15, fontWeight:700, color:E.text, fontFamily:"'Times New Roman',serif" }}>{para.label}</div>
        </div>

        {/* Sentences */}
        {para.sentences.map(s => {
          const key   = para.id+'_'+s.num;
          const val   = writings[key]||'';
          const isKey = s.role.startsWith('★');
          const wc    = val.split(/\s+/).filter(Boolean).length;

          return (
            <div key={s.id} style={{
              background:'#ffffff',
              border:`1px solid ${isKey?E.btnPrimary+'88':'#c0c0c0'}`,
              borderLeft: isKey?`4px solid ${E.btnPrimary}`:'1px solid #c0c0c0',
              borderRadius:6, marginBottom:14,
              boxShadow:'0 1px 3px rgba(0,0,0,0.07)',
            }}>

              {/* Sentence header */}
              <div style={{ padding:'10px 14px', borderBottom:'1px solid #ebebeb', display:'flex', gap:8, alignItems:'flex-start', background:isKey?E.accentBg:'#fafafa' }}>
                <div style={{ background:E.btnPrimary, color:'#ffffff', fontFamily:"'Courier New',monospace", fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:3, flexShrink:0, marginTop:2, letterSpacing:'.05em' }}>{s.num}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13.5, fontWeight:700, color:isKey?E.btnPrimary:E.label, marginBottom:3, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.role}</div>
                  <div style={{ fontSize:12, color:E.muted, lineHeight:1.55, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.purpose}</div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button onClick={() => setShowIdeas(x => ({...x,[key]:!x[key]}))}
                    style={{ fontSize:11.5, padding:'4px 10px', borderRadius:4, border:`1px solid ${showIdeas[key]?E.gold:'#c0c0c0'}`, background:showIdeas[key]?E.warningBg:'#ffffff', color:showIdeas[key]?E.warning:E.muted, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:showIdeas[key]?600:400 }}>
                    💡 Ideas
                  </button>
                  <button onClick={() => setShowF(x => ({...x,[key]:!x[key]}))}
                    style={{ fontSize:11.5, padding:'4px 10px', borderRadius:4, border:`1px solid ${showF[key]?E.btnPrimary:'#c0c0c0'}`, background:showF[key]?E.accentBg:'#ffffff', color:showF[key]?E.btnPrimary:E.muted, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:showF[key]?600:400 }}>
                    {showF[key]?'Hide':'Formula'}
                  </button>
                </div>
              </div>

              {/* Idea bank */}
              {showIdeas[key] && s.ideas && (
                <div style={{ background:E.warningBg, borderBottom:'1px solid #e8d8a0', padding:'12px 16px' }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10.5, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:E.warning, marginBottom:10 }}>💡 Idea Bank — suggestions to inspire your writing</div>
                  {s.ideas.map((idea,ii) => (
                    <div key={ii} style={{ fontSize:13, color:'#3a2e00', lineHeight:1.7, padding:'6px 10px 6px 14px', borderLeft:'3px solid #d4a017', marginBottom:6, background:'rgba(255,255,255,0.6)', borderRadius:'0 4px 4px 0', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                      {idea}
                    </div>
                  ))}
                </div>
              )}

              {/* Formula options */}
              {showF[key] && (
                <div style={{ background:'#f8faff', borderBottom:'1px solid #d0ddf0', padding:'12px 16px' }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10.5, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:E.accent, marginBottom:10 }}>Formula Options — click to use</div>
                  {s.opts.map((opt,oi) => {
                    const sel = selOpt[key] === opt;
                    return (
                      <div key={oi}
                        onClick={() => { setSelOpt(x => ({...x,[key]:opt})); setW(key,opt); }}
                        style={{ background:sel?E.accentBg:'#ffffff', border:`1.5px solid ${sel?E.btnPrimary:'#c8d8f0'}`, borderRadius:5, padding:'10px 14px', marginBottom:8, cursor:'pointer', transition:'all .15s', boxShadow:sel?`0 0 0 2px ${E.btnPrimary}22`:'none' }}>
                        <div style={{ fontFamily:"'Courier New',monospace", fontSize:9.5, color:sel?E.btnPrimary:'#888', marginBottom:4, fontWeight:700 }}>OPTION {oi+1}</div>
                        <div style={{ fontSize:13, color:E.text, lineHeight:1.65, fontFamily:"'Times New Roman',serif" }}>{opt}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Writing area */}
              <div style={{ padding:'12px 14px' }}>
                <textarea
                  value={val}
                  onChange={e => setW(key, e.target.value)}
                  placeholder={`Write your ${s.role.replace('★ ','')} here…`}
                  rows={4}
                  style={{ width:'100%', background:'#ffffff', border:`1.5px solid ${val.length>10?E.btnPrimary:'#c0c0c0'}`, borderRadius:4, padding:'10px 12px', color:E.text, fontSize:14, lineHeight:1.8, fontFamily:"'Times New Roman', Times, serif", outline:'none', resize:'vertical', transition:'border-color .2s' }}
                />
                {val.trim().length > 8 && (
                  <div style={{ fontSize:11.5, color:E.success, marginTop:4, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>✓ {wc} word{wc!==1?'s':''}</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Feedback button */}
        <div style={{ marginBottom:16 }}>
          {paraDone ? (
            <button onClick={handleFeedback} disabled={!!loadingFb[para.id]}
              style={{ width:'100%', background:loadingFb[para.id]?'#6a8ab0':E.btnPrimary, color:E.btnText, border:'none', padding:'13px', borderRadius:5, fontSize:14, fontWeight:600, cursor:loadingFb[para.id]?'default':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 6px rgba(26,95,168,0.3)', letterSpacing:'0.01em' }}>
              {loadingFb[para.id] ? '⏳ Analysing your paragraph…' : fb ? `Re-analyse ${paraLabels[para.id]}` : `Get AI Feedback on ${paraLabels[para.id]}`}
            </button>
          ) : (
            <div style={{ textAlign:'center', fontSize:12.5, color:E.muted, padding:'12px', background:'#f5f5f5', borderRadius:5, border:'1px solid #c0c0c0', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Complete all {para.sentences.length} sentences above to unlock AI feedback
            </div>
          )}
          {fbError[para.id] && (
            <div style={{ marginTop:10, padding:'10px 14px', background:E.errorBg, border:'1px solid #e0a0a0', borderRadius:5, fontSize:13, color:E.error, lineHeight:1.6, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              ⚠ {fbError[para.id]}
            </div>
          )}
        </div>

        {/* Feedback display */}
        {fb && (
          <div style={{ marginBottom:20 }}>
            <FeedbackDisplay feedback={fb} title={paraLabels[para.id]}/>
          </div>
        )}

        {/* Navigation */}
        <div style={{ display:'flex', gap:10, marginBottom:24 }}>
          {activePara > 0 && (
            <button onClick={() => { setActivePara(activePara-1); window.scrollTo(0,0); }}
              style={{ flex:1, background:'#ffffff', border:'1px solid #c0c0c0', color:E.label, padding:'11px', borderRadius:5, cursor:'pointer', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 1px 3px rgba(0,0,0,0.07)' }}>
              ← {paraLabels[paras[activePara-1].id]}
            </button>
          )}
          {activePara < paras.length-1 && (
            <button onClick={() => { setActivePara(activePara+1); window.scrollTo(0,0); }}
              style={{ flex:1, background:E.btnPrimary, border:'none', color:'#ffffff', padding:'11px', borderRadius:5, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 6px rgba(26,95,168,0.3)' }}>
              Next: {paraLabels[paras[activePara+1].id]} →
            </button>
          )}
        </div>

        {/* Complete essay */}
        {allDone && (
          <div style={{ background:'#ffffff', border:'1px solid #c0c0c0', borderRadius:6, overflow:'hidden', marginBottom:28, boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ background:E.navBg, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#ffffff', fontFamily:"'Times New Roman',serif" }}>Your Complete Essay</div>
              <div style={{ fontFamily:"'Courier New',monospace", fontSize:12, color:totalWC>=250?'#7adb98':'#ffcc66', background:'rgba(0,0,0,0.2)', padding:'3px 12px', borderRadius:3 }}>
                {totalWC} words {totalWC>=250?'✓ Minimum met':'— need '+(250-totalWC)+' more'}
              </div>
            </div>
            <div style={{ padding:'20px 22px' }}>
              {paras.map((p,pi) => {
                const txt = p.sentences.map(s => writings[p.id+'_'+s.num]||'').filter(Boolean).join(' ');
                if (!txt) return null;
                return (
                  <div key={p.id} style={{ marginBottom:18, paddingBottom:18, borderBottom:pi<paras.length-1?'1px solid #e8e8e8':'none' }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:E.accent, marginBottom:6 }}>{p.para} — {paraLabels[p.id]}</div>
                    <div style={{ fontSize:14.5, lineHeight:1.9, color:E.text, fontFamily:"'Times New Roman',serif" }}>{txt}</div>
                  </div>
                );
              })}
              {!submitted ? (
                <button onClick={handleSubmit}
                  style={{ marginTop:8, width:'100%', background:E.btnPrimary, color:'#ffffff', border:'none', padding:'13px', borderRadius:5, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 6px rgba(26,95,168,0.3)' }}>
                  ✓ Submit Essay
                </button>
              ) : (
                <div style={{ marginTop:12 }}>
                  <div style={{ textAlign:'center', fontSize:14, color:E.success, fontWeight:600, fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:14 }}>✓ Essay submitted successfully!</div>
                  <button onClick={onBack}
                    style={{ width:'100%', background:E.navBg, color:'#ffffff', border:'none', padding:'13px', borderRadius:5, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}>
                    ← Back
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ background:E.navBg, padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, color:'#ffffff' }}>IELTS<span style={{color:E.gold}}>COMPASS</span></div>
        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
          {[branding?.phone1||'00201008182425', branding?.phone2||'00971565727742'].map(p => (
            <span key={p} style={{ fontFamily:"'Courier New',monospace", fontSize:11, color:'rgba(255,255,255,0.55)' }}>📞 {p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
